import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FormError } from '@/components/ui/form-field';
import { Label } from '@/components/ui/label';
import { Modal } from '@/components/ui/modal';
import { Typography } from '@/components/ui/typography';
import { Lock, Loader2 } from 'lucide-react';

import { IconComponent } from '../../../../util/icons';
import visaIcon from '../../../../asset/images/visa.png';
import amexIcon from '../../../../asset/images/amex.svg';
import discoverIcon from '../../../../asset/images/discover.svg';
import masterIcon from '../../../../asset/icons/masterIcon.svg';

import { useCardForm } from '../hooks/useCardForm';
import { CardType } from '../../../../types/order.types';
import type { SavedCard } from '../../../../types/order.types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CardFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isUpdate: boolean;
  isSingleCard: boolean;
  cardDetails?: SavedCard;
  onSuccess: () => void;
}

// ---------------------------------------------------------------------------
// Card icon helper
// ---------------------------------------------------------------------------

function CardIcon({ type }: { type: CardType | null }) {
  if (!type) return null;
  if (type === CardType.VISA)
    return <IconComponent src={visaIcon} alt="visaIcon" width={40} height={12} />;
  if (type === CardType.MASTERCARD)
    return <IconComponent src={masterIcon} alt="masterIcon" width={40} height={20} />;
  if (type === CardType.AMEX)
    return <IconComponent src={amexIcon} alt="amexIcon" width={40} height={12} />;
  if (type === CardType.DISCOVER)
    return <IconComponent src={discoverIcon} alt="discoverIcon" width={40} height={12} />;
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CardFormModal({
  open,
  onOpenChange,
  isUpdate,
  isSingleCard,
  cardDetails,
  onSuccess,
}: CardFormModalProps) {
  const form = useCardForm({
    open,
    isUpdate,
    isSingleCard,
    cardDetails,
    onSuccess,
    onClose: () => onOpenChange(false),
  });

  const title = (
    <div className="flex items-center gap-2">
      <Lock className="h-5 w-5" />
      <Typography className="text-lg font-semibold">
        {isUpdate ? 'Update Card Details' : 'Add Card Details'}
      </Typography>
    </div>
  );

  const exceptSymbols = ['e', 'E', '+', '-', '.'];

  return (
    <Modal open={open} onOpenChange={onOpenChange} size="md" title={title}>
      <div className="space-y-4">
        {/* Card number + CVC row */}
        <div className="grid grid-cols-12 gap-4">
          {/* DV-2789: in update mode the CVC field is hidden, so the card-number block takes the
              full row instead of leaving an empty right-third. */}
          <div className={isUpdate ? 'col-span-12' : 'col-span-12 sm:col-span-8'}>
            <Label htmlFor="credit-card-number" required>Credit Card Number</Label>
            <div className="relative">
              {form.displayCardType && (
                <span className="absolute left-2 top-1/2 -translate-y-1/2 z-10">
                  <CardIcon type={form.displayCardType} />
                </span>
              )}
              {/* DV-2789 (PCI-DSS 3.4): data-private keeps LogRocket from recording the PAN
                  keystrokes; the network sanitizer only covers request/response bodies. */}
              <Input
                id="credit-card-number"
                data-private
                ref={form.inputNumberRef}
                type="text"
                autoComplete="off"
                value={isUpdate ? form.getEditDisplayValue('cardNumber') : form.cardNumber}
                maxLength={19}
                placeholder="0000 0000 0000 0000"
                disabled={isUpdate}
                onKeyDown={form.handleCardNumberKeyDown}
                onPaste={form.handlePaste}
                onInput={(e) => form.handleCardNumberChange((e.target as HTMLInputElement).value)}
                className={`${form.displayCardType ? 'pl-12' : ''} ${
                  !form.isValidCCNumber ? 'border-destructive' : ''
                }`}
                data-testid="credit-card-number_id"
              />
            </div>
            <FormError message={!form.isValidCCNumber ? 'Invalid credit card number' : undefined} />
          </div>
          {/* DV-2789: the CVV isn't stored and can't be edited on a saved card, so the CVC field
              is only shown when adding a card. Rendering it disabled+empty on update looked broken. */}
          {!isUpdate && (
            <div className="col-span-12 sm:col-span-4">
              <Label htmlFor="credit-card-cvc" required>CVC</Label>
              <Input
                id="credit-card-cvc"
                data-private
                ref={form.inputCVCRef}
                type="password"
                autoComplete="off"
                maxLength={form.cvcMaxLength}
                placeholder="CVC"
                onInput={(e) => form.setCardCVC((e.target as HTMLInputElement).value)}
                className={!form.isValidCardCVC ? 'border-destructive' : ''}
                data-testid="credit-card-cvc_id"
              />
              <FormError message={!form.isValidCardCVC ? 'Invalid CVC' : undefined} />
            </div>
          )}
        </div>

        {/* Name + Expiry row */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 sm:col-span-8">
            <Label htmlFor="credit-card-name" required>Name on Card</Label>
            <Input
              id="credit-card-name"
              type="text"
              autoComplete="off"
              defaultValue={isUpdate ? form.getEditDisplayValue('name') : undefined}
              placeholder="Enter Card Name"
              onInput={(e) => form.setCardName((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => { if (e.key === ' ') e.stopPropagation(); }}
              className={!form.isValidCCName ? 'border-destructive' : ''}
              data-testid="credit-card-name_id"
            />
            <FormError message={!form.isValidCCName ? 'Invalid credit card name' : undefined} />
          </div>
          <div className="col-span-12 sm:col-span-4">
            <Label htmlFor="credit-card-expiry-month" required>Expiration</Label>
            <div className="flex items-center gap-1">
              <Input
                id="credit-card-expiry-month"
                data-private
                ref={form.inputMonthRef}
                type="text"
                autoComplete="off"
                maxLength={2}
                value={form.expiryMonth}
                placeholder="MM"
                onKeyDown={form.handleMonthKeyDown}
                onBlur={form.handleMonthBlur}
                onInput={(e) => form.handleMonthInput((e.target as HTMLInputElement).value)}
                className={!form.isValidCardExpiryDate ? 'border-destructive' : ''}
                aria-invalid={!form.isValidCardExpiryDate}
                aria-describedby="credit-card-expiry-error"
                data-testid="credit-card-expiry-month_id"
              />
              <span aria-hidden="true" className="text-muted-foreground">/</span>
              <Input
                id="credit-card-expiry-year"
                data-private
                ref={form.inputYearRef}
                type="text"
                autoComplete="off"
                maxLength={2}
                value={form.expiryYear}
                placeholder="YY"
                onBlur={form.handleYearBlur}
                onInput={(e) => form.handleYearInput((e.target as HTMLInputElement).value)}
                className={!form.isValidCardExpiryDate ? 'border-destructive' : ''}
                aria-invalid={!form.isValidCardExpiryDate}
                aria-describedby="credit-card-expiry-error"
                data-testid="credit-card-expiry-year_id"
              />
            </div>
            <FormError
              id="credit-card-expiry-error"
              message={!form.isValidCardExpiryDate ? 'Expiration must be a valid future date' : undefined}
            />
          </div>
        </div>

        {/* Zip code row */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 sm:col-span-8">
            <Label htmlFor="zipCode" required>ZIP Code</Label>
            <Input
              id="zipCode"
              type="text"
              autoComplete="off"
              defaultValue={isUpdate ? form.getEditDisplayValue('zipCode') : undefined}
              disabled={isUpdate}
              maxLength={5}
              placeholder="Enter ZIP"
              onBlur={(e) => form.setZipCode(e.target.value)}
              onKeyDown={(e) =>
                (exceptSymbols.includes(e.key) || isNaN(+e.key)) &&
                e.key !== 'Backspace' &&
                e.key !== 'Tab' &&
                e.preventDefault()
              }
              className={!form.isValidZipCode ? 'border-destructive' : ''}
              data-testid="zipCode_id"
            />
            <FormError message={!form.isValidZipCode ? 'Invalid zip code' : undefined} />
          </div>
        </div>

        {/* Checkboxes */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="default-card"
              disabled={isUpdate && isSingleCard}
              checked={form.defaultCard}
              onCheckedChange={(checked) => form.setDefaultCard(checked === true)}
              data-testid="checkbox_id"
            />
            <Label htmlFor="default-card" className="mb-0 font-normal">Set as Default Card</Label>
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="terms"
              checked={isUpdate || form.terms}
              onCheckedChange={(checked) => form.setTerms(checked === true)}
            />
            <Label htmlFor="terms" required className="mb-0 font-normal">
              I allow PlanOmatic to directly charge the card for any future
              purchases in accordance with their terms. I understand that I can
              cancel this permission at any time.
            </Label>
          </div>
        </div>

        {/* Footer buttons */}
        <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={form.isButtonDisabled() || form.loading}
            onClick={form.onSave}
            className="w-full sm:w-auto"
          >
            {form.loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {isUpdate ? 'Update card' : 'Save card'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
